import { omit } from 'lodash';
import { useCallback, useMemo } from 'react';

import { useDataMutation, useDataProvider, useUser } from '@work4all/data';
import { useTenant } from '@work4all/data/lib/hooks/routing/TenantProvider';

import { User } from '@work4all/models/lib/Classes/User.entity';
import { UserSyncOptions } from '@work4all/models/lib/Classes/UserSyncOptions.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

const USER_DATA: User = {
  id: null,
  syncOptions: [
    {
      id: null,
      mailboxId: null,
      contactsExportCustomer: null,
      contactsExportSupplier: null,
      contactsExportEmployees: null,
      contactsExportMode: null,
      contactsExportAllowed: null,
      contactsIcludePrivateData: null,
      appointmentsSync: null,
      appointmentsSyncPrivateItems: null,
      tenantCode: null,
      contactsExportCustomerAllowed: null,
      contactsExportSupplierAllowed: null,
      contactsExportEmployeesAllowed: null,
      appointmentsSyncExternalAppointments: null,
      appointmentsAllowPrivate: null,
    },
  ],
};

export const useUserSyncOptions = () => {
  const user = useUser();
  const { activeTenant } = useTenant();
  const req = useMemo<DataRequest>(() => {
    return {
      entity: Entities.user,
      data: USER_DATA,
      filter: [{ id: { $eq: user.benutzerCode } }],
    };
  }, [user]);

  const { data: userData } = useDataProvider<User>(req);

  const [mutate] = useDataMutation({
    entity: Entities.user,
    mutationType: EMode.upsert,
    responseData: USER_DATA,
  });

  const mutateSyncOptions = useCallback(
    (syncOptions: UserSyncOptions) => {
      if (!syncOptions.id) {
        syncOptions.id = undefined;
      }

      return mutate(
        {
          id: userData?.[0]?.id,
        },
        {
          relations: {
            syncOptions: {
              addModify: [
                {
                  ...omit(syncOptions, [
                    '__typename',
                    'contactsExportCustomerAllowed',
                    'contactsExportSupplierAllowed',
                    'contactsExportEmployeesAllowed',
                    'contactsExportAllowed',
                  ]),
                  tenantCode: activeTenant,
                },
              ],
            },
          },
        }
      );
    },
    [mutate, userData, activeTenant]
  );

  return {
    syncOptions: userData?.[0]?.syncOptions?.[0] || USER_DATA.syncOptions[0],
    mutateSyncOptions,
  };
};
